import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { getProxiedImageUrl, getProductPath } from '../utils/helpers';

const Wishlist = () => {
  const { t, language } = useLanguage();
  const { wishlist, removeFromWishlist, clearWishlist } = useWishlist();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  if (wishlist.length === 0) {
    return (
      <div className="dark-container pt-20">
        <section className="py-20 px-6 lg:px-[7.6923%]">
          <div className="max-w-[1400px] mx-auto text-center">
            <div className="mb-8">
              <Heart size={80} className="mx-auto mb-6 text-white/20" />
              <h1 className="display-large mb-4">
                {language === 'it' ? 'Lista Preferiti Vuota' : 'Wishlist Empty'}
              </h1>
              <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
                {language === 'it' 
                  ? 'Non hai ancora aggiunto prodotti ai preferiti. Inizia a esplorare!' 
                  : 'You haven\'t added any products to your wishlist yet. Start exploring!'}
              </p>
              <Link to="/products" className="btn-primary inline-flex items-center gap-2">
                {language === 'it' ? 'Esplora Prodotti' : 'Browse Products'}
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dark-container pt-20">
      {/* Page Header */}
      <section className="py-20 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="display-large mb-4">
                {language === 'it' ? 'I Miei Preferiti' : 'My Wishlist'}
              </h1>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                {language === 'it' 
                  ? `${wishlist.length} prodotti salvati` 
                  : `${wishlist.length} saved products`}
              </p>
            </div>
            {wishlist.length > 0 && (
              <button
                onClick={clearWishlist}
                className="flex items-center gap-2 px-6 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                <Trash2 size={20} />
                {language === 'it' ? 'Svuota Lista' : 'Clear All'}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Wishlist Grid */}
      <section className="py-20 px-6 lg:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto">
          <div className="dark-grid">
            {wishlist.map((product) => (
              <div key={product.sku} className="dark-card overflow-hidden relative">
                {/* Remove button */}
                <button
                  onClick={() => removeFromWishlist(product.sku)}
                  className="absolute top-4 right-4 z-10 p-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full transition-colors"
                  title={language === 'it' ? 'Rimuovi dai preferiti' : 'Remove from wishlist'}
                >
                  <X size={18} />
                </button>

                <Link to={getProductPath(product)} className="block">
                  <div className="aspect-square overflow-hidden bg-white relative">
                    {product.stock === 0 && (
                      <div className="absolute top-4 left-4 z-10 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                        {language === 'it' ? 'ESAURITO' : 'OUT OF STOCK'}
                      </div>
                    )}
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={getProxiedImageUrl(product.images[0])}
                        alt={language === 'it' ? product.name_it : product.name_en}
                        className={`w-full h-full object-contain p-4 dark-transition hover:scale-110 ${product.stock === 0 ? 'opacity-60' : ''}`}
                        loading="lazy"
                        style={{ backgroundColor: '#ffffff' }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">
                        {language === 'it' ? 'Immagine non disponibile' : 'No Image'}
                      </div>
                    )}
                  </div>
                </Link>

                <div className="p-6">
                  <div className="text-sm mb-2" style={{ color: 'var(--brand-primary)' }}>
                    {product.category}
                  </div>
                  <Link to={getProductPath(product)}>
                    <h3 className="text-xl font-semibold mb-3 hover:text-primary transition-colors">
                      {language === 'it' ? product.name_it : product.name_en}
                    </h3>
                  </Link>
                  <p className="body-medium mb-4 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                    {language === 'it' 
                      ? (product.description_it || '').substring(0, 100) + '...'
                      : (product.description_en || '').substring(0, 100) + '...'}
                  </p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-3xl font-bold" style={{ color: 'var(--brand-primary)' }}>
                      €{product.price ? product.price.toFixed(2) : '0.00'}
                    </div>
                    {product.stock > 0 ? (
                      <div className="text-sm px-3 py-1 bg-green-500/20 text-green-400">
                        {language === 'it' ? 'Disponibile' : 'In Stock'}
                      </div>
                    ) : (
                      <div className="text-sm px-3 py-1 bg-red-500/20 text-red-400">
                        {language === 'it' ? 'Esaurito' : 'Out of Stock'}
                      </div>
                    )}
                  </div>

                  {/* Add to Cart Button */}
                  <button 
                    className={`w-full py-3 px-4 rounded font-semibold transition-all flex items-center justify-center gap-2 ${
                      product.stock === 0 
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                        : 'btn-primary'
                    }`}
                    disabled={product.stock === 0}
                    onClick={(e) => {
                      e.preventDefault();
                      if (product.stock > 0) {
                        addToCart(product);
                      }
                    }}
                  >
                    <ShoppingCart size={20} />
                    {product.stock === 0 
                      ? (language === 'it' ? 'Non Disponibile' : 'Unavailable')
                      : (language === 'it' ? 'Aggiungi al Carrello' : 'Add to Cart')
                    }
                  </button>

                  {product.brand && (
                    <div className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                      {language === 'it' ? 'Marca' : 'Brand'}: <span className="font-semibold">{product.brand}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Continue Shopping */}
          <div className="text-center mt-12">
            <Link to="/products" className="btn-secondary inline-flex items-center gap-2">
              {language === 'it' ? 'Continua lo Shopping' : 'Continue Shopping'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Wishlist;
